// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — games in a meeting (`docs/AYNI_XR_MEETING_GAMES_PLAN.md`): the
 * shapes the room and the browsers exchange, every limit the room
 * enforces, the scoring, and the validation of everything that arrives.
 *
 * The room is the referee (`gameReferee.ts`): the presenter's page builds
 * each round, because it has the data loaded, and sends it with its answer;
 * the room passes the round on without the answer, takes one answer per
 * player, scores it, and tells each connection what it may see. Players
 * sit at tables of up to TABLE_SIZE (decision D1) and are known only by a
 * funny name the room gives them (`gameNames.ts`, decision D2). Games are
 * for meetings whose host's plan lends them (Education, decision D3).
 *
 * Like `roomActivity.ts`: names, shapes, limits and validation only,
 * imported by the browser and by the room's Durable Object.
 */

import { ACTIVITY_OPTION_CHARS, ACTIVITY_PROMPT_CHARS, cleanText, distanceKm, parsePoint } from './roomActivity'
import { isGameName, type GameName } from './gameNames'
import { parseTableAction, type TableAction, type TableOptions, type TableSummary, type TableView } from './gameTables'

export type { GameName } from './gameNames'

/**
 * The games: trivia (from a Khipu's quizzes), Maypi (where on Earth?),
 * Wayra (a table follows a clue trail), Team Earth (a table plans the
 * decades). The last two run AYNI 3D's engines per table.
 */
export type GameKind = 'trivia' | 'maypi' | 'wayra' | 'earth'
export const GAME_KINDS: readonly GameKind[] = ['trivia', 'maypi', 'wayra', 'earth']

/**
 * Where a game is: players gathering at their tables, a round taking
 * answers (or, once closed, waiting for its results), a round's results
 * and the board, the final board.
 */
export type GamePhase = 'lobby' | 'round' | 'reveal' | 'over'

export type Difficulty = 'easy' | 'medium' | 'hard'

/** What the presenter asks to start. */
export interface GameStart {
  kind: GameKind
  /** What the game is called on screen (a Khipu's title, the game's name). */
  title: string
  /** A table game's choices (Wayra, Team Earth). */
  options?: TableOptions
}

/** Whether a game is played by AYNI 3D's engines, one per table. */
export const isTableKind = (kind: GameKind): kind is 'wayra' | 'earth' => kind === 'wayra' || kind === 'earth'

/**
 * A round as the presenter's page sends it, answer included. The room
 * never passes the answer on before the round is revealed.
 */
export type RoundSpec =
  | {
      kind: 'choice'
      prompt: string
      options: string[]
      /** The right option's index. */
      correct: number
      difficulty: Difficulty
      durationMs: number
    }
  | {
      kind: 'point'
      prompt: string
      /**
       * The right place, scored by distance; null when the presenter's page
       * scores the pins itself from the data on its globe (a data round:
       * "the hottest place right now"), after the round closes.
       */
      answer: [number, number] | null
      /** What the right place is called, shown at the reveal; '' for none. */
      label: string
      /** A line about it, shown at the reveal (a place's postcard); '' for none. */
      fact: string
      /** Full marks within this distance of the answer, km. */
      radiusKm: number
      /** How fast marks fall beyond it, km (Maypi's forgiveness scale). */
      scaleKm: number
      durationMs: number
    }

/** Players at one table (decision D1). */
export const TABLE_SIZE = 4
/** Most players in one game: the meeting size games were built for; not load tested beyond it (2026-10-17). */
export const GAME_MAX_PLAYERS = 50
/** Name re-rolls each player gets (decision G3). */
export const NAME_REROLLS = 1
/** The board's top rows shown to everyone (decision G2). */
export const BOARD_TOP = 5
export const GAME_TITLE_CHARS = 120
/** Answer times the interface offers, in seconds. */
export const ROUND_DURATIONS_S = [15, 20, 30, 45, 60] as const
export const ROUND_MIN_MS = 10_000
export const ROUND_MAX_MS = 5 * 60_000
/** How long the room waits for the presenter's page to score a data round before scoring it zero. */
export const EVAL_TIMEOUT_MS = 15_000
/** Times one player may change an answer in one round. */
export const GAME_ANSWER_CHANGES = 10
export const GAME_MAX_OPTIONS = 6
export const GAME_MIN_OPTIONS = 2
/** Most rounds in one game, so a board and a log stay small. */
export const GAME_MAX_ROUNDS = 60
/** A player's secret: kept in the tab, sent on joining, never shown to anyone. */
const KEY_PATTERN = /^[A-Za-z0-9_-]{16,64}$/
const ID_PATTERN = /^[\w-]{1,40}$/

// ------------------------------------------------------------------ scoring

/** Trivia: AYNI 3D's trivia formula (base × difficulty + time bonus), five times its points so a round weighs like Maypi's. */
export const TRIVIA_BASE_POINTS = 500
export const TRIVIA_TIME_BONUS = 250
export const DIFFICULTY_MULTIPLIERS: Readonly<Record<Difficulty, number>> = { easy: 1, medium: 1.5, hard: 2 }
/** Maypi: points = 1000 × accuracy × speed, speed falling from 1 to 0.8 at the deadline (AYNI 3D's `geo-game/scoring.ts`). */
export const POINT_MAX = 1000
export const POINT_SPEED_FLOOR = 0.8

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v))

/** Points for a right trivia answer given `takenMs` into a round of `durationMs`. */
export function triviaPoints(difficulty: Difficulty, takenMs: number, durationMs: number): number {
  const left = durationMs > 0 ? clamp01(1 - takenMs / durationMs) : 0
  return Math.round(TRIVIA_BASE_POINTS * DIFFICULTY_MULTIPLIERS[difficulty] + TRIVIA_TIME_BONUS * left)
}

/** Maypi's speed factor: 1 for an instant answer, 0.8 at the deadline. */
export function pointSpeed(takenMs: number, durationMs: number): number {
  const left = durationMs > 0 ? clamp01(1 - takenMs / durationMs) : 0
  return POINT_SPEED_FLOOR + (1 - POINT_SPEED_FLOOR) * left
}

/** Accuracy of a pin `km` from the answer: full within the radius, then e^(−(d−r)/s). */
export function pointAccuracy(km: number, radiusKm: number, scaleKm: number): number {
  if (km <= radiusKm) return 1
  return Math.exp(-(km - radiusKm) / Math.max(1, scaleKm))
}

export function pointPoints(accuracy: number, speed: number): number {
  return Math.max(0, Math.round(POINT_MAX * clamp01(accuracy) * speed))
}

/** A pin's distance to a point round's answer, km. */
export const pinDistanceKm = (pin: [number, number], answer: [number, number]): number => distanceKm(pin, answer)

/**
 * Maypi's data rounds: accuracy from the percentile the pin's value reaches
 * in the frame (AYNI 3D's GEO_PERCENTILE_ACCURACY): the top of a field is a
 * plateau, so a value near the top earns nearly as much as the top itself.
 * Read on the presenter's page, which has the frame.
 */
const PERCENTILE_ACCURACY: ReadonlyArray<readonly [number, number]> = [
  [0.75, 0],
  [0.9, 0.3],
  [0.95, 0.55],
  [0.98, 0.8],
  [0.99, 1],
]

export function percentileAccuracy(percentile: number): number {
  const p = clamp01(percentile)
  const first = PERCENTILE_ACCURACY[0]
  if (p <= first[0]) return 0
  for (let i = 1; i < PERCENTILE_ACCURACY.length; i++) {
    const [p1, a1] = PERCENTILE_ACCURACY[i]
    const [p0, a0] = PERCENTILE_ACCURACY[i - 1]
    if (p <= p1) return a0 + ((p - p0) / (p1 - p0)) * (a1 - a0)
  }
  return 1
}

// ------------------------------------------------------------------ the wire

/** A board row: a player's name and total. */
export interface BoardRow {
  name: GameName
  score: number
  /** This row is the viewer's own. */
  you: boolean
}

/** The board (decision G2): the top players, every table, and the viewer's own place, told only to them. */
export interface GameBoard {
  top: BoardRow[]
  /** Tables, best first: number (from 1), total, how many sit there. */
  tables: { n: number; score: number; size: number }[]
  you: { place: number; score: number } | null
}

/** A round's results, for one viewer. */
export interface RoundResult {
  /** A choice round's right option. */
  correct: number | null
  /** A point round's right place (null for a data round, which has many). */
  answer: [number, number] | null
  label: string
  fact: string
  /** How many picked each option (choice). */
  counts: number[] | null
  /** The viewer's points this round; null when they did not play it. */
  points: number | null
  /** The viewer's table's points this round (its players' average). */
  table: number | null
  /** Every pin, for the map at the reveal (point rounds): no names. */
  pins: [number, number][] | null
}

/** A round as one viewer sees it. */
export interface RoundView {
  id: string
  /** 1 for the first round. */
  no: number
  kind: 'choice' | 'point'
  prompt: string
  options: string[] | null
  durationMs: number
  remainingMs: number
  elapsedMs: number
  /** No more answers: time is up, everyone answered, or the presenter closed it. */
  closed: boolean
  /** Waiting for the presenter's page to score a data round. */
  scoring: boolean
  /** Whether the viewer may answer it (a player who was here when it started). */
  playing: boolean
  /** The viewer's answer so far. */
  choice: number | null
  pin: [number, number] | null
  result: RoundResult | null
}

/** A player as the presenter and moderators see them: never a typed name, never the secret key. */
export interface TeacherPlayer {
  pid: string
  /** Their connection while here (to match a raised hand), else null. */
  conn: string | null
  name: GameName
  /** 0-based. */
  table: number
  score: number
  answered: boolean
}

export interface TeacherView {
  players: TeacherPlayer[]
  /** Answers in the round, of players who may answer it. */
  answered: number
  eligible: number
  /** A closed data round's pins for the presenter's page to score: [pid, lat, lon]. */
  toScore: [string, number, number][] | null
  /** Choice rounds: answers per option so far. */
  counts: number[] | null
}

/** What one connection sees of the game. */
export interface GameView {
  id: string
  kind: GameKind
  title: string
  phase: GamePhase
  /** Rounds started so far. */
  rounds: number
  tables: number
  players: number
  round: RoundView | null
  /** The viewer as a player; null for the presenter, a moderator, or before joining. */
  you: { pid: string; name: GameName; table: number; score: number; rerolls: number } | null
  /** The names at the viewer's table, the viewer's own excluded. */
  mates: GameName[]
  board: GameBoard | null
  teacher: TeacherView | null
  /** A table game: the viewer's table as its engine shows it (the kiosk view); null otherwise. */
  table: TableView | null
  /** A table game: the viewer's table's outcome so far. */
  tableResult: { score: number; stars: number } | null
  /** A table game, for the presenter and moderators: every table. */
  tableGames: TableSummary[] | null
}

// ---------------------------------------------------------------- parsing

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const isId = (v: unknown): v is string => typeof v === 'string' && ID_PATTERN.test(v)

export const isGameKind = (v: unknown): v is GameKind => typeof v === 'string' && (GAME_KINDS as readonly string[]).includes(v)
export const isGameKey = (v: unknown): v is string => typeof v === 'string' && KEY_PATTERN.test(v)
export const isDifficulty = (v: unknown): v is Difficulty => v === 'easy' || v === 'medium' || v === 'hard'

export function parseGameStart(raw: unknown): GameStart | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isGameKind(r.kind)) return null
  const title = cleanText(r.title, GAME_TITLE_CHARS)
  if (!title) return null
  if (!isTableKind(r.kind)) return { kind: r.kind, title }
  return { kind: r.kind, title, options: parseTableOptions(r.options) }
}

/** A table game's choices, each kept only when it is one the engines take. */
export function parseTableOptions(raw: unknown): TableOptions {
  const o = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>
  const out: TableOptions = {}
  if (o.stops === 3 || o.stops === 5) out.stops = o.stops
  if (o.clueSeconds === 15 || o.clueSeconds === 20 || o.clueSeconds === 30) out.clueSeconds = o.clueSeconds
  if (o.rounds === 3 || o.rounds === 5) out.rounds = o.rounds
  if (o.planSeconds === 45 || o.planSeconds === 60 || o.planSeconds === 90) out.planSeconds = o.planSeconds
  const h = o.home as Record<string, unknown> | null | undefined
  if (h && typeof h === 'object') {
    const at = parsePoint([h.lat, h.lon])
    const name = cleanText(h.name, 60)
    if (at && name) out.home = { name, lat: at[0], lon: at[1] }
  }
  return out
}

function parseDuration(raw: unknown): number | null {
  if (!finite(raw) || raw < ROUND_MIN_MS || raw > ROUND_MAX_MS) return null
  return Math.round(raw)
}

/** A round the presenter sent, made safe, or null. */
export function parseRoundSpec(raw: unknown): RoundSpec | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  const prompt = cleanText(r.prompt, ACTIVITY_PROMPT_CHARS)
  const durationMs = parseDuration(r.durationMs)
  if (!prompt || durationMs === null) return null
  if (r.kind === 'choice') {
    if (!Array.isArray(r.options)) return null
    const options = (r.options as unknown[]).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS))
    if (options.some((o) => o === null) || options.length < GAME_MIN_OPTIONS || options.length > GAME_MAX_OPTIONS) return null
    if (!Number.isInteger(r.correct) || (r.correct as number) < 0 || (r.correct as number) >= options.length) return null
    return { kind: 'choice', prompt, options: options as string[], correct: r.correct as number, difficulty: isDifficulty(r.difficulty) ? r.difficulty : 'medium', durationMs }
  }
  if (r.kind === 'point') {
    const answer = r.answer === null ? null : parsePoint(r.answer)
    if (r.answer !== null && !answer) return null
    const radiusKm = finite(r.radiusKm) && r.radiusKm >= 0 && r.radiusKm <= 5000 ? r.radiusKm : 50
    const scaleKm = finite(r.scaleKm) && r.scaleKm >= 50 && r.scaleKm <= 10000 ? r.scaleKm : 2000
    return { kind: 'point', prompt, answer, label: cleanText(r.label, ACTIVITY_OPTION_CHARS) ?? '', fact: cleanText(r.fact, ACTIVITY_PROMPT_CHARS) ?? '', radiusKm, scaleKm, durationMs }
  }
  return null
}

/** The presenter's page scoring a data round: [pid, accuracy 0..1] pairs. */
export function parseScores(raw: unknown): [string, number][] | null {
  if (!Array.isArray(raw) || raw.length > GAME_MAX_PLAYERS) return null
  const out: [string, number][] = []
  for (const item of raw as unknown[]) {
    if (!Array.isArray(item) || item.length !== 2 || !isId(item[0]) || !finite(item[1])) return null
    out.push([item[0], clamp01(item[1])])
  }
  return out
}

function parseBoard(raw: unknown): GameBoard | null {
  if (typeof raw !== 'object' || raw === null) return null
  const b = raw as Record<string, unknown>
  if (!Array.isArray(b.top) || !Array.isArray(b.tables)) return null
  const top: BoardRow[] = []
  for (const item of b.top as unknown[]) {
    const r = item as Record<string, unknown> | null
    if (!r || !isGameName(r.name) || !finite(r.score)) return null
    top.push({ name: r.name, score: r.score, you: r.you === true })
  }
  const tables: GameBoard['tables'] = []
  for (const item of b.tables as unknown[]) {
    const r = item as Record<string, unknown> | null
    if (!r || !Number.isInteger(r.n) || !finite(r.score) || !Number.isInteger(r.size)) return null
    tables.push({ n: r.n as number, score: r.score, size: r.size as number })
  }
  const y = b.you as Record<string, unknown> | null | undefined
  const you = y && Number.isInteger(y.place) && finite(y.score) ? { place: y.place as number, score: y.score } : null
  return { top, tables, you }
}

const pointOrNull = (v: unknown): [number, number] | null => (v === null || v === undefined ? null : parsePoint(v))

function parseResult(raw: unknown): RoundResult | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  const numOrNull = (v: unknown): number | null => (finite(v) ? v : null)
  return {
    correct: Number.isInteger(r.correct) ? (r.correct as number) : null,
    answer: pointOrNull(r.answer),
    label: cleanText(r.label, ACTIVITY_OPTION_CHARS) ?? '',
    fact: cleanText(r.fact, ACTIVITY_PROMPT_CHARS) ?? '',
    counts: Array.isArray(r.counts) && r.counts.every((c) => finite(c) && c >= 0) ? (r.counts as number[]) : null,
    points: numOrNull(r.points),
    table: numOrNull(r.table),
    pins: Array.isArray(r.pins) ? (r.pins as unknown[]).map(parsePoint).filter((p): p is [number, number] => p !== null) : null,
  }
}

function parseRoundView(raw: unknown): RoundView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!isId(r.id) || !Number.isInteger(r.no) || (r.kind !== 'choice' && r.kind !== 'point')) return null
  const prompt = cleanText(r.prompt, ACTIVITY_PROMPT_CHARS)
  if (!prompt || !finite(r.durationMs) || !finite(r.remainingMs) || !finite(r.elapsedMs)) return null
  let options: string[] | null = null
  if (r.kind === 'choice') {
    if (!Array.isArray(r.options)) return null
    options = (r.options as unknown[]).map((o) => cleanText(o, ACTIVITY_OPTION_CHARS) ?? '')
  }
  return {
    id: r.id,
    no: r.no as number,
    kind: r.kind,
    prompt,
    options,
    durationMs: r.durationMs,
    remainingMs: Math.max(0, r.remainingMs),
    elapsedMs: Math.max(0, r.elapsedMs),
    closed: r.closed === true,
    scoring: r.scoring === true,
    playing: r.playing === true,
    choice: Number.isInteger(r.choice) ? (r.choice as number) : null,
    pin: pointOrNull(r.pin),
    result: r.result ? parseResult(r.result) : null,
  }
}

function parseTeacher(raw: unknown): TeacherView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (!Array.isArray(r.players) || !finite(r.answered) || !finite(r.eligible)) return null
  const players: TeacherPlayer[] = []
  for (const item of r.players as unknown[]) {
    const p = item as Record<string, unknown> | null
    if (!p || !isId(p.pid) || !isGameName(p.name) || !Number.isInteger(p.table) || !finite(p.score)) return null
    players.push({ pid: p.pid, conn: typeof p.conn === 'string' && p.conn.length <= 64 ? p.conn : null, name: p.name, table: p.table as number, score: p.score, answered: p.answered === true })
  }
  let toScore: [string, number, number][] | null = null
  if (Array.isArray(r.toScore)) {
    toScore = []
    for (const item of r.toScore as unknown[]) {
      if (!Array.isArray(item) || item.length !== 3 || !isId(item[0])) continue
      const p = parsePoint([item[1], item[2]])
      if (p) toScore.push([item[0], p[0], p[1]])
    }
  }
  const counts = Array.isArray(r.counts) && r.counts.every((c) => finite(c) && c >= 0) ? (r.counts as number[]) : null
  return { players, answered: r.answered, eligible: r.eligible, toScore, counts }
}

/** A game view as the room sent it, or null. */
export function parseGameView(raw: unknown): GameView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const v = raw as Record<string, unknown>
  if (!isId(v.id) || !isGameKind(v.kind)) return null
  if (v.phase !== 'lobby' && v.phase !== 'round' && v.phase !== 'reveal' && v.phase !== 'over') return null
  const title = cleanText(v.title, GAME_TITLE_CHARS) ?? ''
  if (!Number.isInteger(v.rounds) || !Number.isInteger(v.tables) || !Number.isInteger(v.players)) return null
  const y = v.you as Record<string, unknown> | null | undefined
  const you = y && isId(y.pid) && isGameName(y.name) && Number.isInteger(y.table) && finite(y.score) && Number.isInteger(y.rerolls)
    ? { pid: y.pid, name: y.name, table: y.table as number, score: y.score, rerolls: y.rerolls as number }
    : null
  return {
    id: v.id,
    kind: v.kind,
    title,
    phase: v.phase,
    rounds: v.rounds as number,
    tables: v.tables as number,
    players: v.players as number,
    round: v.round ? parseRoundView(v.round) : null,
    you,
    mates: Array.isArray(v.mates) ? (v.mates as unknown[]).filter(isGameName) : [],
    board: v.board ? parseBoard(v.board) : null,
    teacher: v.teacher ? parseTeacher(v.teacher) : null,
    table: parseTableView(v.table),
    tableResult: parseResultPair(v.tableResult),
    tableGames: Array.isArray(v.tableGames) ? (v.tableGames as unknown[]).map(parseTableSummary).filter((s): s is TableSummary => s !== null) : null,
  }
}

/**
 * A table's kiosk view, as the room built it from AYNI 3D's engine. It
 * carries no text anyone typed (places, clues and cards are the engines'
 * own content), so only its shape is checked: the engine and a phase.
 */
function parseTableView(raw: unknown): TableView | null {
  if (typeof raw !== 'object' || raw === null) return null
  const t = raw as { engine?: unknown; v?: unknown }
  if ((t.engine !== 'wayra' && t.engine !== 'earth') || typeof t.v !== 'object' || t.v === null) return null
  if (typeof (t.v as { phase?: unknown }).phase !== 'string') return null
  return t as TableView
}

function parseResultPair(raw: unknown): { score: number; stars: number } | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  return finite(r.score) && finite(r.stars) ? { score: r.score, stars: r.stars } : null
}

function parseTableSummary(raw: unknown): TableSummary | null {
  if (typeof raw !== 'object' || raw === null) return null
  const s = raw as Record<string, unknown>
  if (!Number.isInteger(s.n) || typeof s.phase !== 'string' || !finite(s.at) || !finite(s.of) || !finite(s.score) || !finite(s.stars) || !finite(s.size)) return null
  return { n: s.n as number, phase: s.phase.slice(0, 20), at: s.at, of: s.of, score: s.score, stars: s.stars, size: s.size }
}

/** Browser → room, for games. */
export type GameClientMessage =
  /** Presenter: start a game (any running one is replaced). */
  | { t: 'game'; g: GameStart }
  /** Presenter: start the tables' games (Wayra, Team Earth), close the open round now, end the game (the final board), or take it down. */
  | { t: 'gctl'; a: 'start' | 'close' | 'end' | 'remove' }
  /** Presenter: the next round. */
  | { t: 'ground'; r: RoundSpec }
  /** Presenter: a closed data round's accuracy per player. */
  | { t: 'gscore'; id: string; acc: [string, number][] }
  /** Presenter, before the first round: seat a player at another table (0-based). */
  | { t: 'gmove'; pid: string; table: number }
  /** A participant joins the game, or comes back to it, with the secret its tab keeps. */
  | { t: 'gjoin'; key: string }
  /** A player: another name, once. */
  | { t: 'greroll' }
  /** A player's answer to the open round. */
  | { t: 'gans'; id: string; choice: number | null; p: [number, number] | null }
  /** A player at a table game: a vote, the next clue, on to the next stop… (`gameTables.ts`). */
  | { t: 'gplay'; a: TableAction }

/** Parse a game message (the caller has the JSON object); null when it is none or not valid. */
export function parseGameClientMessage(msg: Record<string, unknown>): GameClientMessage | null {
  switch (msg.t) {
    case 'game': {
      const g = parseGameStart(msg.g)
      return g ? { t: 'game', g } : null
    }
    case 'gctl':
      return msg.a === 'start' || msg.a === 'close' || msg.a === 'end' || msg.a === 'remove' ? { t: 'gctl', a: msg.a } : null
    case 'ground': {
      const r = parseRoundSpec(msg.r)
      return r ? { t: 'ground', r } : null
    }
    case 'gscore': {
      const acc = parseScores(msg.acc)
      return isId(msg.id) && acc ? { t: 'gscore', id: msg.id, acc } : null
    }
    case 'gmove':
      return isId(msg.pid) && Number.isInteger(msg.table) && (msg.table as number) >= 0 && (msg.table as number) < GAME_MAX_PLAYERS
        ? { t: 'gmove', pid: msg.pid, table: msg.table as number }
        : null
    case 'gjoin':
      return isGameKey(msg.key) ? { t: 'gjoin', key: msg.key } : null
    case 'greroll':
      return { t: 'greroll' }
    case 'gplay': {
      const a = parseTableAction(msg.a)
      return a ? { t: 'gplay', a } : null
    }
    case 'gans': {
      if (!isId(msg.id)) return null
      const p = msg.p === null || msg.p === undefined ? null : parsePoint(msg.p)
      const choice = Number.isInteger(msg.choice) && (msg.choice as number) >= 0 && (msg.choice as number) < GAME_MAX_OPTIONS ? (msg.choice as number) : null
      if ((p === null) === (choice === null)) return null
      return { t: 'gans', id: msg.id, choice, p }
    }
    default:
      return null
  }
}

/** A fresh secret for this tab's player in one meeting. */
export function newGameKey(random: () => number = Math.random): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let key = ''
  for (let i = 0; i < 24; i++) key += chars[Math.floor(random() * chars.length)]
  return key
}
